import { GuidedSellingAnswer, GuidedSellingQuestion, GuidedSellingResult } from "@jog/types"
import { useRouter } from "next/router"
import { useCallback, useMemo, useState } from "react"

export const useGuidedSelling = (
    code: string,
    getGuidedSelling: (code: string, answers: [string, string][]) => Promise<GuidedSellingResult>
) => {
    const [step, setStep] = useState(0)
    const [steps, setSteps] = useState(0)
    const [answersArr, setAnswersArr] = useState<[string, string][]>([])
    const [currentSelected, setCurrentSelected] = useState<string[]>([])
    const [currentQuestion, setCurrentQuestion] = useState<GuidedSellingQuestion | null>(null)
    const [loading, setLoading] = useState(false)
    const [navigateLoading, setNavigateLoading] = useState(false)
    const [dropdownShow, setDropdownShow] = useState(false)
    const router = useRouter()
    const getGuidedSellingWithCode = useCallback(
        async (answers: [string, string][]) => {
            setLoading(true)
            return getGuidedSelling(code, answers)
                .then((res: GuidedSellingResult) => {
                    if (!res.questions) return
                    const isEnd = res?.questions.every((question) => !question.current)
                    if (isEnd) {
                        setNavigateLoading(true)
                        router.push(
                            `/guided-selling/${code}${res.navigationurl}&from=${encodeURIComponent(window.location.href)}`
                        )
                    } else {
                        window?.scrollTo({ top: 0 })
                        setSteps(res?.questions.length)
                        const index = res?.questions.findIndex((question) => question.current)
                        if (index !== -1) {
                            setStep(index)
                            setCurrentQuestion(res.questions[index])
                        } else {
                            setCurrentQuestion(null)
                        }
                    }
                })
                .finally(() => {
                    setLoading(false)
                })
        },
        [code, getGuidedSelling, router]
    )

    const onClickMultiple = useCallback(
        (answer: GuidedSellingAnswer) => {
            const isExist = currentSelected.some((item) => item === `${answer.answerid}`)
            const newSelectedArr = isExist
                ? currentSelected.filter((item) => item !== `${answer.answerid}`)
                : [...currentSelected, `${answer.answerid}`]
            setCurrentSelected(newSelectedArr)
        },
        [currentSelected]
    )

    const onClickSingle = useCallback(
        (answer: GuidedSellingAnswer) => {
            if (dropdownShow) setDropdownShow(false)
            setCurrentSelected([`${answer.answerid}`])
        },
        [dropdownShow]
    )

    const nextStep = useCallback(
        async (answerArr: [string, string][]) => {
            setAnswersArr(answerArr)
            await getGuidedSellingWithCode(answerArr)
            setCurrentSelected([])
        },
        [getGuidedSellingWithCode]
    )

    const previousStep = useCallback(() => {
        const lastOne = answersArr.pop() || ["", ""]
        setCurrentSelected(lastOne[1].split(","))
        setAnswersArr(answersArr)
        getGuidedSellingWithCode(answersArr)
    }, [answersArr, getGuidedSellingWithCode])

    const currentSelectedText = useMemo(() => {
        const currentSelectArr =
            currentQuestion?.answers
                .filter((answer) => currentSelected.includes(`${answer.answerid}`))
                .map((item) => item.answer) || []
        return currentSelectArr.join(", ")
    }, [currentQuestion?.answers, currentSelected])

    const isSizeQuestion = useMemo(
        () => currentQuestion?.answers.some((answer) => /(^\d+\/\d+$)|(^\d+$)|(^X*[S|M|L]$)/i.test(answer.answer)),
        [currentQuestion?.answers]
    )

    return {
        step,
        steps,
        answersArr,
        currentSelected,
        currentQuestion,
        loading,
        navigateLoading,
        isSizeQuestion,
        dropdownShow,
        currentSelectedText,
        setDropdownShow,
        onClickMultiple,
        onClickSingle,
        nextStep,
        previousStep,
    }
}
